<script setup lang="ts">
  import { useHttpClient, useViewQuery, useShared, useRoute, useTranslator, useUrlGenerator } from '@othercommerce/foundation';
  import { ref, computed, watch } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';

  const emit = defineEmits(['onFiltersOpen', 'processingChanged']);

  const route = useRoute();
  const query = useViewQuery();
  const shared = useShared();
  const http = useHttpClient();
  const translator = useTranslator();
  const url = useUrlGenerator();

  const processing = ref(false);
  const open = ref(false);
  const type = computed(() => shared.value.productListMode);
  const breakpoints = useBreakpoints();

  watch(processing, (newValue) => {
    emit('processingChanged', newValue);
  });

  watch(() => breakpoints.value.lg, (large) => {
    if (large) {
      open.value = false;
    }
  });

  const sorting = [
    { label: translator.trans('SHOP.PRODUCT.LABEL.SORT_ALPHABETICAL'), value: 'name' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.SORT_BY_LOWEST'), value: 'cheap' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.SORT_BY_HIGHEST'), value: 'expensive' },
    { label: translator.trans('SHOP.PRODUCT.LABEL.SORT_BY_RATING'), value: 'rating' },
  ];

  const items = [
    { label: '12', value: 12 },
    { label: '20', value: 20 },
    { label: '28', value: 28 },
    { label: '36', value: 36 },
    { label: '48', value: 48 },
  ];

  const sort = computed({
    get: () => {
      /*
       * Expect `params` to be an object containing query parameters, like: { sort: 'bla bla bla' }.
       * However, sometimes `params` can be an array (or a Proxy of an array) when no query parameters are present,
       * which causes `params.sort` to reference the native array sort method instead of a query value.
       *
       * To avoid this, we check:
       *  - params exists (not null or undefined)
       *  - params is an object (typeof 'object')
       *  - params is NOT an array (!Array.isArray(params))
       *  - params has the 'sort' key ('sort' in params)
       *
       * Only then we can safely return params.sort.
       * Otherwise, return null to indicate no sorting parameter is set.
      */
      const params = query.value;
      if (params && typeof params === 'object' && !Array.isArray(params) && 'sort' in params) {
        return params.sort;
      }
      return null;
    },
    set: (value) => {
      processing.value = true;

      let filtered = Object.fromEntries(Object.entries(query.value || []).filter(([name, value]) => {
        return name !== 'sort' && !!value;
      }));

      if (value) {
        filtered.sort = value;
      }

      http.get(url.generate(window.location.pathname, filtered)).then(() => {
        processing.value = false;
      });
    },
  });

  const perPage = computed({
    get: () => {
      return shared.value.productListPerPage;
    },
    set: (value) => {
      processing.value = true;
      const currentUrl = new URL(window.location.href);
      http.patch(route('api.v1.web.product.list.per_page'), { per_page: value })
        .then(() => {

          // remove page param, keep everything else (sort, filters, etc.)
          currentUrl.searchParams.delete('page');

          http.get(currentUrl.toString())
            .then(() => {
              processing.value = false;
            });
        });
    },
  });

  function actionToggleMode() {
    let mode = (type.value === 1) ? 0 : 1;

    processing.value = true;
    const currentUrl = new URL(window.location.href);
    http.patch(route('api.v1.web.product.list.mode'), { mode }).then(() => {
      http.get(currentUrl.toString())
        .then(() => {
          processing.value = false;
        });
    });
  }

  function actionOpenFilters() {
    emit('onFiltersOpen');
  }
</script>
<template>
  <div class="products-top-panel">
    <div class="products-top-panel__icons">
      <SfButton
        :loading="processing"
        :aria-busy="processing"
        square
        transparent
        @click="actionToggleMode"
        :title="type === 1
        ? $t('SHOP.PRODUCT.ARIA.DETAIL_VIEW_ENABLED')
        : $t('SHOP.PRODUCT.ARIA.DETAIL_VIEW_DISABLED')"
      >
        <template #before>
          <SfIconShopTile v-if="type === 1" aria-hidden="true" />
          <SfIconShopList v-if="type === 0" aria-hidden="true" />
        </template>
      </SfButton>
    </div>
    <div class="products-top-panel__actions">
      <UiModal v-model:open="open" medium class="products-top-panel__sorting-modal" id="sort-modal" :aria-labelledby="!breakpoints.lg ? 'sort-modal-heading': null">
        <SfModalHeader v-model:open="open" class="products-top-panel__sorting-modal-header" headingId="sort-modal-heading">
          {{ $t('SHOP.COMMON.LABEL.SORT_TYPE') }}
        </SfModalHeader>
        <div class="products-top-panel__sorting-modal-content">
          <div class="products-top-panel__sorting-select-wrapper">
            <SfFormSelect v-model="perPage" :disabled="processing" :label="$t('SHOP.COMMON.LABEL.QUANTITY_PER_PAGE')" :options="items" :placeholder="false" id="products-grid-per-page" required />
          </div>
          <div class="products-top-panel__sorting-select-wrapper products-top-panel__sorting-select-wrapper--sort">
            <SfFormSelect v-model="sort" :disabled="processing" :label="$t('SHOP.COMMON.LABEL.SORT_TYPE')" :options="sorting" :placeholder="$t('SHOP.COMMON.LABEL.SORT_DEFAULT')" id="products-grid-sort" />
          </div>
        </div>
      </UiModal>
      <SfButton class="products-top-panel__filter" :title="$t('SHOP.COMMON.LABEL.FILTERS')" aria-haspopup="dialog" aria-controls="filters-modal" primary square @click="actionOpenFilters" @keydown.enter="actionOpenFilters">
        <template #before>
          <SfIconFunnel aria-hidden="true" />
        </template>
      </SfButton>
      <SfButton class="products-top-panel__filter" :title="$t('SHOP.COMMON.ACTION.SORT')" square @click="open = true" aria-haspopup="dialog" aria-controls="sort-modal">
        <template #before>
          <SfIconSortAlphaDown aria-hidden="true" />
        </template>
      </SfButton>
    </div>
  </div>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .products-top-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--gap-card);
    padding: var(--padding-card);
    background: var(--background-card);
    border: var(--border-card);
    border-radius: var(--border-radius-card);

    @include media.query(lg) {
      border-radius: 0;
      flex-direction: row-reverse;
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      flex: 0 0 22rem;
    }

    &__sorting-modal {
      display: contents;
    }

    :deep(.products-top-panel__sorting-modal.modal) {
      display: contents;

      .modal-container,
      .wrapper,
      .content {
        display: contents;
      }
    }

    &__sorting-modal-header {
      display: none;
    }

    &__sorting-modal-content {
      display: flex;
      gap: 1rem;
    }

    &__sorting-select-wrapper {
      flex: 0 0 8rem;

      &--sort {
        flex: 0 0 13rem;
      }

      @include media.query(lg) {
        flex: 1 0 100%;
      }
    }

    @include media.query(lg) {
      &__actions {
        flex: 0 0 auto;
      }

      :deep(.products-top-panel__sorting-modal.modal) {
        display: revert;

        .modal-container,
        .wrapper,
        .content {
          display: flex;
        }
      }

      &__sorting-modal-header {
        display: flex;
      }

      &__sorting-modal-content {
        flex-direction: column;
      }
    }

    &__filter {
      display: none;

      @include media.query(lg) {
        display: inline-flex;
      }
    }
  }
</style>
